import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FilterCategory } from '@strata/react-filter-pane';

import { Conversation, ConversationFact } from 'api/userSupportAPI';
import {
    ConversationParticipation,
    getUpdatedReferralConversationCounts,
    MappedConversation,
    MappedMessage,
    ReferralConversationCounts,
} from 'services/userSupportHelpers';

export interface UserSupportSliceState {
    socketConnected: boolean;
    connectionLoading: boolean;
    userProfile?: {
        upn: string;
        firstName?: string;
        lastName?: string;
    };
    referralConversations?: Record<string, MappedConversation>;
    currentReferral?: string;
    currentConversation?: string;
    conversationMessages: Record<string, { messages: MappedMessage }>;
    conversationFacts: Record<string, ConversationFact[]>;
    conversationFilterCategories?: FilterCategory<Conversation>[];
    pageData: {
        currentPage: number;
        pages: Record<number, MappedConversation[]>;
        conversationCount?: number;
    };
    userParticipation?: Record<string, string | undefined>;
    conversationParticipation?: Record<string, ConversationParticipation>;
    unreadConversationCount?: number;
    referralConversationCounts?: ReferralConversationCounts;
}

const initialState: UserSupportSliceState = {
    socketConnected: false,
    connectionLoading: true,
    userProfile: undefined,
    referralConversations: undefined,
    currentReferral: undefined,
    currentConversation: undefined,
    conversationMessages: {},
    conversationFacts: {},
    conversationFilterCategories: undefined,
    pageData: {
        currentPage: 0,
        pages: {},
        conversationCount: undefined,
    },
    userParticipation: undefined,
    conversationParticipation: undefined,
    unreadConversationCount: undefined,
    referralConversationCounts: undefined,
};

export const userSupportSlice = createSlice({
    name: 'userSupport',
    initialState,
    reducers: {
        setSocketConnected(state, action: PayloadAction<{ connected: boolean }>) {
            const { connected } = action.payload;
            return {
                ...state,
                socketConnected: connected,
            };
        },
        setConnectionLoading(state, action: PayloadAction<{ loading: boolean }>) {
            const { loading } = action.payload;
            return {
                ...state,
                connectionLoading: loading,
            };
        },
        updateUserProfile(
            state,
            action: PayloadAction<{
                upn: string;
                firstName?: string;
                lastName?: string;
            }>,
        ) {
            const { upn, firstName, lastName } = action.payload;
            return {
                ...state,
                userProfile: {
                    upn,
                    firstName,
                    lastName,
                },
            };
        },
        updateCurrentReferral(state, action: PayloadAction<{ referralId: string } | undefined>) {
            const { referralId } = action.payload ?? {};
            return { ...state, currentReferral: referralId };
        },
        updateReferralConversations(
            state,
            action: PayloadAction<{ conversations?: Record<string, MappedConversation> }>,
        ) {
            const { conversations } = action.payload;
            const { currentReferral } = state;
            if (!conversations) {
                return {
                    ...state,
                    referralConversations: undefined,
                };
            }
            const newReferral = Object.values(conversations)[0]?.referralId;
            const referralChange = newReferral !== currentReferral;
            return {
                ...state,
                currentReferral: newReferral,
                referralConversations: {
                    ...(referralChange ? {} : state.referralConversations),
                    ...conversations,
                },
            };
        },
        updateCurrentConversation(
            state,
            action: PayloadAction<{ conversationId: string } | undefined>,
        ) {
            const { conversationId } = action.payload ?? {};
            return {
                ...state,
                currentConversation: conversationId,
            };
        },
        updateConversationMessages(
            state,
            action: PayloadAction<{
                conversationId: string;
                messages: Record<string, MappedMessage>;
            }>,
        ) {
            const { conversationId, messages } = action.payload;
            return {
                ...state,
                currentConversation: conversationId,
                conversationMessages: {
                    ...state.conversationMessages,
                    [conversationId]: {
                        messages: {
                            ...(state.conversationMessages[conversationId]?.messages ?? {}),
                            ...messages,
                        },
                    },
                },
            };
        },
        setConversationFacts(
            state,
            action: PayloadAction<{ conversationId: string; facts: ConversationFact[] }>,
        ) {
            const { conversationId, facts } = action.payload;
            return {
                ...state,
                conversationFacts: {
                    ...state.conversationFacts,
                    [conversationId]: facts,
                },
            };
        },
        setConversationFilterCategories(
            state,
            action: PayloadAction<FilterCategory<Conversation>[]>,
        ) {
            return {
                ...state,
                conversationFilterCategories: action.payload,
            };
        },
        setCurrentPage(state, action: PayloadAction<{ currentPage: number }>) {
            const { currentPage } = action.payload;
            return {
                ...state,
                pageData: {
                    ...state.pageData,
                    currentPage,
                },
            };
        },
        setPageData(
            state,
            action: PayloadAction<{ page: number; conversations: MappedConversation[] }>,
        ) {
            const { page, conversations } = action.payload;
            return {
                ...state,
                pageData: {
                    ...state.pageData,
                    pages: {
                        ...state.pageData.pages,
                        [page]: conversations,
                    },
                },
            };
        },
        setAdditionalPageData(state, action: PayloadAction<{ conversationCount: number }>) {
            const { conversationCount } = action.payload;
            return {
                ...state,
                pageData: {
                    ...state.pageData,
                    conversationCount,
                },
            };
        },
        resetPageData(state) {
            return { ...state, pageData: initialState.pageData };
        },
        updateUserParticipation(state, action: PayloadAction<Record<string, string | undefined>>) {
            const { userParticipation, conversationParticipation } = state;
            const userUpdates = action.payload;

            const updatedParticipation = {
                ...userParticipation,
                ...userUpdates,
            };

            const updatedConversationCounts = getUpdatedReferralConversationCounts(
                updatedParticipation,
                conversationParticipation,
            );

            return {
                ...state,
                userParticipation: updatedParticipation,
                referralConversationCounts: updatedConversationCounts,
                unreadConversationCount: Object.values(updatedConversationCounts ?? {}).reduce(
                    (prev, curr) => prev + curr.unread,
                    0,
                ),
            };
        },
        updateConversationParticipation(state, action: PayloadAction<ConversationParticipation[]>) {
            const { userParticipation, conversationParticipation } = state;
            const conversationUpdates = action.payload;

            const updatedParticipation = {
                ...conversationParticipation,
                ...Object.fromEntries(
                    conversationUpdates.map((conversation) => [
                        conversation.conversation_id,
                        conversation,
                    ]),
                ),
            };

            const updatedConversationCounts = getUpdatedReferralConversationCounts(
                userParticipation,
                updatedParticipation,
            );

            return {
                ...state,
                conversationParticipation: updatedParticipation,
                referralConversationCounts: updatedConversationCounts,
                unreadConversationCount: Object.values(updatedConversationCounts).reduce(
                    (prev, curr) => prev + curr.unread,
                    0,
                ),
            };
        },
    },
});

export const {
    setSocketConnected,
    setConnectionLoading,
    updateUserProfile,
    updateCurrentReferral,
    updateReferralConversations,
    updateCurrentConversation,
    updateConversationMessages,
    updateUserParticipation,
    updateConversationParticipation,
    setConversationFacts,
    setConversationFilterCategories,
    setCurrentPage,
    setPageData,
    setAdditionalPageData,
    resetPageData,
} = userSupportSlice.actions;
